import { ColDef, TextMatcherParams } from 'ag-grid-community'
import CustomGeneric from './custom-components/custom-generic.vue'
import NikanInput from '../general/nikan-input.vue'
import jalaliMoment from 'jalali-moment'
import DropdownFloatingCenter from '../form/dropdown/dropdown-floating-center.vue'
import DropdownAccount from '../form/dropdown/dropdown-account.vue'
import Dropdown from '~/components/general/dropdown.vue'
// @ts-ignore
import { helpers } from "@nikan/text-visualizer"
import CustomPriceFilterComp from './filters/customPrice/customPriceFilterComp.vue'
import CustomPriceFloatingFilterComp from './filters/customPrice/customPriceFloatingFilterComp.vue'
import DateFilterComp from './filters/date/dateFilterComp.vue'
import DateFloatingFilterComp from './filters/date/dateFloatingFilterComp.vue'
import NikanDate from '../general/nikan-date.vue'
import FrequentDescriptionsInput from '../form/frequent-descriptions/frequent-descriptions-input.vue'
import MultiCurrencyCellView from '../form/multi-currency/multi-currency-cell-view.vue'
import MultiCurrencyCell from '../form/multi-currency/multi-currency-cell.vue'
import { IExtendedColDef } from './interfaces'

const priceReg = /^-?\d+(?:[.,]\d+)*$/

const convertDate = (date: any) => {
  if (date.value?.includes('0001-01-01')) return ''
  return date.value && jalaliMoment(date.value, 'YYYY-M-D ')
    .locale('fa')
    .format('YYYY/MM/DD')
}

const convertDateTime = (date: any) => {
  if (date.value?.includes('0001-01-01')) return ''
  return date.value && jalaliMoment(date.value, 'YYYY-M-DTHH:mm')
    .locale('fa')
    .format('HH:mm YYYY/MM/DD')
}

const tableDefaultDataTypes: { [key: string]: IExtendedColDef } = {
  string: {
    cellEditor: CustomGeneric,
    cellEditorParams: {
      generateProps: (params: any) => {
        return {
          component: NikanInput,
          modelValue: params.data[params.colDef.field],
          options: {
            mode: 'string',
          }
        }
      }
    },
    filter: 'agTextColumnFilter',
  },
  number: {
    cellEditor: CustomGeneric,
    cellDataType: 'number',
    cellEditorParams: {
      generateProps: (params: any) => {
        return {
          component: NikanInput,
          modelValue: params.data[params.colDef.field],
          options: {
            mode: 'integer',
          }
        }
      }
    },
    aggregateFunction: 'sum',
    filter: 'agNumberColumnFilter',
  },
  price: {
    valueFormatter: params => {
      return String(parseFloat(String(Math.abs(Number(params.value)))))
    },
    cellRenderer: (params: any) => {
      if (priceReg.exec(params.value))
        return helpers.formatPrice(Math.abs(params.value))
      else return params.value
    },
    comparator: (valueA, valueB, nodeA, nodeB) => {
      valueA = Number(valueA)
      valueB = Number(valueB)
      if (valueA == valueB) return 0
      return (valueA > valueB) ? 1 : -1
    },
    filter: CustomPriceFilterComp,
    floatingFilterComponent: CustomPriceFloatingFilterComp,
    cellEditor: CustomGeneric,
    cellEditorParams: {
      generateProps: (params: any) => {
        return {
          component: NikanInput,
          modelValue: params.data[params.colDef.field],
          options: {
            mode: 'price',
          },
        }
      }
    }
  },
  date: {
    filter: DateFilterComp,
    floatingFilterComponent: DateFloatingFilterComp,
    cellRenderer: (params: any) => convertDate(params),
    valueFormatter: (params: any) => convertDate(params),
    cellEditor: CustomGeneric,
    cellEditorParams: {
      generateProps: (params: any) => {
        return {
          component: NikanDate,
          modelValue: params.data[params.colDef.field],
          options: {
            pickerEnabled: true,
            stopOnEdges: true,
            emptyValue: null
          }
        }
      }
    },
  },
  dateTime: {
    filter: DateFilterComp,
    floatingFilterComponent: DateFloatingFilterComp,
    cellRenderer: (params: any) => convertDateTime(params),
    valueFormatter: (params) => convertDateTime(params),
    cellEditor: CustomGeneric,
    cellEditorParams: {
      generateProps: (params: any) => {
        return {
          component: NikanDate,
          modelValue: params.data[params.colDef.field],
          options: {
            pickerEnabled: true,
            stopOnEdges: true,
            emptyValue: null
          }
        }
      }
    },
  },
  account: {
    cellRenderer: (params: any) => {
      return params.value ? `${params.value.name} (${params.value.accountingCode})` : ''
    },
    valueFormatter: (params: any) => {
      return params.value ? `${params.value.name} (${params.value.accountingCode})` : ''
    },
    cellEditor: CustomGeneric,
    cellEditorParams: {
      generateProps: (params: any) => {
        return {
          component: DropdownAccount,
          modelValue: params.data[params.colDef.field],
          options: {
            showSelectedBalance: false,
          }
        }
      }
    },
    filter: 'agTextColumnFilter',
    filterParams: {
      textMatcher: (params: TextMatcherParams) => {
        const text = params.colDef.cellRenderer({ ...params, value: params.data[params.colDef.field as string] })

        if (params.filterText == null) {
          return false
        }

        switch (params.filterOption) {
          case 'contains':
            return text.indexOf(params.filterText) >= 0
          case 'notContains':
            return text.indexOf(params.filterText) < 0
          case 'equals':
            return text === params.filterText
          case 'notEqual':
            return text != params.filterText
          case 'startsWith':
            return text.indexOf(params.filterText) === 0
          case 'endsWith':
            const index = text.lastIndexOf(params.filterText)
            return index >= 0 && index === (text.length - params.filterText.length)
          default:
            return false
        }
      }
    },
    comparator: (valueA, valueB, nodeA, nodeB) => {
      return (valueA?.name || '').localeCompare((valueB?.name || ''))
    },
  },
  dropdown: {
    cellEditor: CustomGeneric,
    cellEditorParams: {
      generateProps: (params: any) => {
        return {
          component: Dropdown,
          modelValue: params.data[params.colDef.field],
          options: {}
        }
      }
    },
    filter: 'agTextColumnFilter',
    filterParams: {
      textMatcher: (params: TextMatcherParams) => {
        const text = params.colDef.cellRenderer({ ...params, value: params.data[params.colDef.field as string] })

        if (params.filterText == null) {
          return false
        }

        switch (params.filterOption) {
          case 'contains':
            return text.indexOf(params.filterText) >= 0
          case 'notContains':
            return text.indexOf(params.filterText) < 0
          case 'equals':
            return text === params.filterText
          case 'notEqual':
            return text != params.filterText
          case 'startsWith':
            return text.indexOf(params.filterText) === 0
          case 'endsWith':
            const index = text.lastIndexOf(params.filterText)
            return index >= 0 && index === (text.length - params.filterText.length)
          default:
            return false
        }
      }
    },
    comparator: (valueA, valueB, nodeA, nodeB) => {
      return (valueA?.name || '').localeCompare((valueB?.name || ''))
    },
  },
  center: {
    cellRenderer: (params: any) => {
      return params.value ? `${params.value.name} (${params.value.accountingCode})` : ''
    },
    valueFormatter: (params: any) => {
      return params.value ? `${params.value.name} (${params.value.accountingCode})` : ''
    },
    cellEditor: CustomGeneric,
    cellEditorParams: {
      generateProps: (params: any) => {
        return {
          component: DropdownFloatingCenter,
          modelValue: params.data[params.colDef.field],
          options: {
            showSelectedBalance: false,
          }
        }
      }
    },
    filter: 'agTextColumnFilter',
    filterParams: {
      textMatcher: (params: TextMatcherParams) => {
        const text = params.colDef.cellRenderer({ ...params, value: params.data[params.colDef.field as string] })

        if (params.filterText == null) {
          return false
        }

        switch (params.filterOption) {
          case 'contains':
            return text.indexOf(params.filterText) >= 0
          case 'notContains':
            return text.indexOf(params.filterText) < 0
          case 'equals':
            return text === params.filterText
          case 'notEqual':
            return text != params.filterText
          case 'startsWith':
            return text.indexOf(params.filterText) === 0
          case 'endsWith':
            const index = text.lastIndexOf(params.filterText)
            return index >= 0 && index === (text.length - params.filterText.length)
          default:
            return false
        }
      }
    },
    comparator: (valueA, valueB, nodeA, nodeB) => {
      return (valueA?.name || '').localeCompare((valueB?.name || ''))
    },
  },
  description: {
    cellEditor: CustomGeneric,
    cellEditorParams: {
      generateProps: (params: any) => {
        return {
          component: FrequentDescriptionsInput,
          modelValue: params.data[params.colDef.field],
        }
      }
    },
    filter: 'agTextColumnFilter',
  },
  multiCurrency: {
    cellRenderer: MultiCurrencyCellView,
    cellEditor: CustomGeneric,
    cellEditorParams: {
      generateProps: (params: any) => {
        return {
          component: MultiCurrencyCell,
          modelValue: params.data[params.colDef.field],
        }
      }
    },
    valueFormatter: (params: any) => {
      const targetField = params.colDef.cellRendererParams?.options?.targetValuePropertyName
      const cellData = params.data[params.colDef.field]

      if (!targetField)
        return cellData

      return targetField === 'currency' ? cellData?.[0]?.[targetField]?.name || '' : cellData?.[0]?.[targetField]
    },
    filter: CustomPriceFilterComp,
    floatingFilterComponent: CustomPriceFloatingFilterComp,
    filterParams: {
      targetValue: (params: any, column: any) => {
        const targetField = column.cellRendererParams?.options?.targetValuePropertyName

        if (!targetField)
          return ''

        return params.data[column.field]?.[0]?.[targetField]
      }
    },
  },
  rowNumber: {
    sort: 'asc',
    suppressSizeToFit: true,
    lockPosition: 'right',
    aggregateFunction: (values) => {
      if (!values) return ''

      return `(${values.length})`
    },
    valueGetter: (params) => {
      if (params.data[params.colDef.field as any])
        return params.data[params.colDef.field as any]

      return params.data?.displayOrder ? params.data.displayOrder : (params.node?.rowIndex || 0) + 1
    },
    cellRenderer: (params: any) => {
      if (params.node.rowPinned)
        return params.value

      return `<div class="row-state-container"><span class="row-state-circle ${params.data.rowState}"></span> <span>${params.value}</span></div>`
    }
  }
}

export default tableDefaultDataTypes